import type { ViewBody } from '@ayumia/contracts';
import { text } from '../../app/i18n';
import type { BlockKind, ChangeClass, EditorNode, Finding } from '../editor/editor-types';
import { formatPointer, getAt, parsePointer, type Pointer } from '../editor/json-pointer';

/**
 * Profil « vue » du socle d'éditeur (ADR-130 tranche 2b) : comment une vue de liste (`view-body` 3.6.0) se
 * lit en arbre de blocs — colonnes, filtres proposés, filtres enregistrés, recherche, tri par défaut,
 * regroupement —, quels blocs la palette ajoute (les champs du catalogue servi par `GetViewStudio`), et les
 * contrôles locaux. Pur : aucune interface, aucun appel. Le serveur reste l'autorité (`ValidateViewDraft`).
 */

export type View = ViewBody;

/** Champ du catalogue (`GetViewStudio.fields`) : ce que le modèle, la politique et les index en disent. */
export interface CatalogueField {
  field_id: string;
  label: unknown;
  type: string;
  origin?: string;
  inheritance_state?: string;
  envelope?: boolean;
  filterable: boolean;
  searchable: boolean;
  sortable: boolean;
  indexed: boolean | null;
  readable_by: string[];
  blocked_for: string[];
  enum_values?: string[];
}

export type Catalogue = readonly CatalogueField[];

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

export const TYPE_GLYPHS: Readonly<Record<string, string>> = { string: 'Aa', rich_text: 'Aa', number: '1,0', decimal: '1,0', quantity: '1,0', money: '€', boolean: '✓', date: 'JJ', datetime: 'JJ', duration: 'h', enumeration: '≡', reference: '→', collection: '[ ]', file: '⎙', json: '{ }' };

export const fieldLabel = (catalogue: Catalogue, fieldId: string): string => {
  const field = catalogue.find((entry) => entry.field_id === fieldId);
  return field === undefined ? fieldId : text(field.label as Record<string, string> | undefined, 'fr', fieldId);
};

/** Index de la représentation Table (colonnes), sinon de la première liste (cartes) ; `-1` sans liste. */
export function tableIndex(view: View): number {
  const table = view.representations.findIndex((representation) => representation.component === 'Table');
  return table >= 0 ? table : view.representations.findIndex((representation) => representation.component === 'CardList');
}

export const columnsPointer = (view: View): Pointer => `/representations/${tableIndex(view)}/inputs/columns`;
const FILTERS = '/collection/inputs/filters';
const QUICK = '/collection/inputs/quick_filters/items';
const SEARCH = '/collection/inputs/search';
const PERIOD = '/collection/inputs/period';
const SORT = '/data_source/default_sort';
export const groupPointer = (view: View): Pointer => `/representations/${tableIndex(view)}/inputs/group_by`;

const asStrings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : typeof value === 'string' ? [value] : []);
const columnsOf = (view: View): Array<Record<string, unknown> & { field: string }> => {
  const list = getAt(view, columnsPointer(view));
  return Array.isArray(list) ? (list as Array<Record<string, unknown> & { field: string }>) : [];
};

/** Identifiant libre d'un filtre enregistré. */
export function freshQuickFilterId(view: View): string {
  const used = new Set(((getAt(view, QUICK) as Array<{ quick_filter_id?: string }> | undefined) ?? []).map((item) => item.quick_filter_id));
  let index = 1;
  while (used.has(`filtre_${index}`)) index += 1;
  return `filtre_${index}`;
}

function fieldKind(field: CatalogueField, create: () => unknown): BlockKind<View> {
  return { id: field.field_id, label: text(field.label as Record<string, string> | undefined, 'fr', field.field_id), glyph: TYPE_GLYPHS[field.type] ?? '?', create };
}

/** Marques d'une colonne : ce que la politique et le modèle en disent, affichage seulement. */
function columnMarks(column: Record<string, unknown> & { field: string }, catalogue: Catalogue): string[] {
  const field = catalogue.find((entry) => entry.field_id === column.field);
  const marks: string[] = [];
  if (field === undefined) return ['inconnu'];
  if (field.readable_by.length === 0) marks.push('illisible');
  else if (field.blocked_for.length > 0) marks.push('lecture partielle');
  if (field.origin === 'customer') marks.push('champ client');
  if (column['label'] !== undefined) marks.push('renommée');
  if (column['cell'] !== undefined || column['tones'] !== undefined) marks.push('format');
  return marks;
}

/** Arbre des blocs d'une vue de liste. */
export function viewTree(view: View, catalogue: Catalogue): EditorNode<View> {
  const children: EditorNode<View>[] = [];
  const table = tableIndex(view);
  if (table >= 0 && view.representations[table]!.component === 'Table') {
    const list = columnsPointer(view);
    const columns = columnsOf(view);
    const shown = new Set(columns.map((column) => column.field));
    children.push({
      pointer: list,
      kind: 'columns',
      label: 'Colonnes',
      detail: `${columns.length}`,
      glyph: '▥',
      insert: [{ list, label: 'Ajouter une colonne', kinds: catalogue.filter((field) => !shown.has(field.field_id) && field.readable_by.length > 0).map((field) => fieldKind(field, () => ({ field: field.field_id }))) }],
      children: columns.map((column, index) => ({
        pointer: `${list}/${index}`,
        kind: 'column',
        label: column['label'] === undefined ? fieldLabel(catalogue, column.field) : text(column['label'] as Record<string, string>, 'fr', column.field),
        detail: column.field,
        glyph: TYPE_GLYPHS[catalogue.find((entry) => entry.field_id === column.field)?.type ?? ''] ?? '?',
        position: { list, index, size: columns.length, minimum: 1, group: 'column' },
        marks: columnMarks(column, catalogue),
        children: [],
      })),
    });
  }

  const filters = asStrings(getAt(view, FILTERS));
  const period = getAt(view, PERIOD) as { field?: string } | undefined;
  const filterChildren: EditorNode<View>[] = filters.map((fieldId, index) => ({
    pointer: `${FILTERS}/${index}`,
    kind: 'filter',
    label: fieldLabel(catalogue, fieldId),
    detail: fieldId,
    glyph: '⌕',
    position: { list: FILTERS, index, size: filters.length, group: 'filter' },
    marks: catalogue.find((entry) => entry.field_id === fieldId)?.indexed === false ? ['non indexé'] : [],
    children: [],
  }));
  if (period?.field !== undefined) filterChildren.push({ pointer: PERIOD, kind: 'period', label: `Période — ${fieldLabel(catalogue, period.field)}`, detail: period.field, glyph: 'JJ', children: [] });
  children.push({
    pointer: FILTERS,
    kind: 'filters',
    label: 'Filtres proposés',
    detail: `${filterChildren.length}`,
    glyph: '⌕',
    insert: [
      { list: FILTERS, label: 'Proposer un filtre', kinds: catalogue.filter((field) => field.filterable && !filters.includes(field.field_id) && field.field_id !== 'entity_id').map((field) => fieldKind(field, () => field.field_id)) },
    ],
    children: filterChildren,
  });

  const quick = (getAt(view, QUICK) as Array<{ quick_filter_id: string; label?: unknown; default?: boolean; filter?: Record<string, unknown> }> | undefined) ?? [];
  children.push({
    pointer: QUICK,
    kind: 'quick_filters',
    label: 'Filtres enregistrés',
    detail: `${quick.length}`,
    glyph: '☰',
    insert: [{ list: QUICK, label: 'Enregistrer un filtre', kinds: [{ id: 'quick_filter', label: 'Filtre enregistré', glyph: '☰', create: (current) => ({ quick_filter_id: freshQuickFilterId(current), label: { fr: 'Nouveau filtre' }, filter: {} }) }] }],
    children: quick.map((item, index) => ({
      pointer: `${QUICK}/${index}`,
      kind: 'quick_filter',
      label: text(item.label as Record<string, string> | undefined, 'fr', item.quick_filter_id),
      detail: Object.keys(item.filter ?? {}).join(', ') || 'tout',
      glyph: '☰',
      position: { list: QUICK, index, size: quick.length, group: 'quick' },
      marks: item.default === true ? ['par défaut'] : [],
      children: [],
    })),
  });

  const search = getAt(view, SEARCH) as { fields?: string[] } | undefined;
  children.push({ pointer: SEARCH, kind: 'search', label: 'Recherche', detail: search?.fields === undefined ? 'aucune' : search.fields.map((fieldId) => fieldLabel(catalogue, fieldId)).join(', '), glyph: '⌕', children: [] });

  const sorts = (getAt(view, SORT) as Array<{ field: string; direction: string }> | undefined) ?? [];
  children.push({
    pointer: SORT,
    kind: 'sorts',
    label: 'Tri par défaut',
    detail: `${sorts.length}`,
    glyph: '⇅',
    insert: [{ list: SORT, label: 'Trier par', kinds: catalogue.filter((field) => field.sortable && !sorts.some((sort) => sort.field === field.field_id)).map((field) => fieldKind(field, () => ({ field: field.field_id, direction: 'asc' }))) }],
    children: sorts.map((sort, index) => ({
      pointer: `${SORT}/${index}`,
      kind: 'sort',
      label: fieldLabel(catalogue, sort.field),
      detail: sort.direction === 'desc' ? 'décroissant' : 'croissant',
      glyph: sort.direction === 'desc' ? '↓' : '↑',
      position: { list: SORT, index, size: sorts.length, group: 'sort' },
      children: [],
    })),
  });

  if (table >= 0) {
    const group = asStrings(getAt(view, groupPointer(view)));
    children.push({ pointer: groupPointer(view), kind: 'group', label: 'Regroupement', detail: group.length === 0 ? 'aucun' : group.map((fieldId) => fieldLabel(catalogue, fieldId)).join(' › '), glyph: '⊞', children: [] });
  }
  return { pointer: '', kind: 'view', label: text(view.title as Record<string, string> | undefined, 'fr', 'Vue'), glyph: '▤', children };
}

/** Sorte du bloc à un pointeur (`column`, `filter`…), ou `null`. */
export function blockKindAt(view: View, pointer: Pointer): string | null {
  if (pointer === '') return 'view';
  const columns = columnsPointer(view);
  if (pointer === columns) return 'columns';
  if (pointer.startsWith(`${columns}/`) && parsePointer(pointer).length === parsePointer(columns).length + 1) return 'column';
  if (pointer === FILTERS) return 'filters';
  if (/^\/collection\/inputs\/filters\/\d+$/.test(pointer)) return 'filter';
  if (pointer === PERIOD) return 'period';
  if (pointer === QUICK) return 'quick_filters';
  if (/^\/collection\/inputs\/quick_filters\/items\/\d+$/.test(pointer)) return 'quick_filter';
  if (pointer === SEARCH) return 'search';
  if (pointer === SORT) return 'sorts';
  if (/^\/data_source\/default_sort\/\d+$/.test(pointer)) return 'sort';
  if (pointer === groupPointer(view)) return 'group';
  return null;
}

/** Bloc qui porte un chemin de constat : la colonne, le filtre, le tri…, sinon la vue. */
export function nodeOfPath(view: View, path: string): Pointer {
  const segments = parsePointer(path);
  for (let length = segments.length; length > 0; length -= 1) {
    const candidate = formatPointer(segments.slice(0, length));
    if (blockKindAt(view, candidate) !== null) return candidate;
  }
  return '';
}

/**
 * Contrôles locaux immédiats, d'après le catalogue servi : colonne en double ou inconnue, filtre non
 * filtrable, recherche non cherchable, champ que personne ne lit (erreurs) ; lecture partielle, filtre ou
 * tri non indexé (avertissements). Les mêmes règles que le serveur, qui tranche.
 */
export function localFindings(view: View, catalogue: Catalogue): Finding[] {
  const findings: Finding[] = [];
  const byId = new Map(catalogue.map((field) => [field.field_id, field]));
  const access = (path: string, fieldId: string, noun: string): CatalogueField | undefined => {
    const field = byId.get(fieldId);
    if (field === undefined) {
      findings.push({ severity: 'error', code: 'VIEW_FIELD_UNKNOWN', path, message: `« ${fieldId} » n’est pas un champ de l’objet.` });
      return undefined;
    }
    if (field.readable_by.length === 0) findings.push({ severity: 'error', code: 'VIEW_FIELD_NOT_READABLE', path, message: `${noun} cite « ${fieldId} », qu’aucun rôle qui ouvre la vue ne peut lire.` });
    else if (field.blocked_for.length > 0) findings.push({ severity: 'warning', code: 'VIEW_FIELD_PARTIALLY_READABLE', path, message: `« ${fieldId} » n’est pas lisible par ${field.blocked_for.join(', ')}.` });
    return field;
  };
  const columns = columnsOf(view);
  const seen = new Set<string>();
  columns.forEach((column, index) => {
    const path = `${columnsPointer(view)}/${index}/field`;
    if (seen.has(column.field)) findings.push({ severity: 'error', code: 'VIEW_COLUMN_DUPLICATE', path, message: `La colonne « ${column.field} » est déjà dans la liste.` });
    seen.add(column.field);
    access(path, column.field, 'La colonne');
  });
  if (tableIndex(view) >= 0 && view.representations[tableIndex(view)]!.component === 'Table' && columns.length === 0) findings.push({ severity: 'error', code: 'VIEW_COLUMNS_EMPTY', path: columnsPointer(view), message: 'Une liste garde au moins une colonne.' });
  const filtered = (path: string, fieldId: string, noun: string): void => {
    const field = access(path, fieldId, noun);
    if (field === undefined) return;
    if (!field.filterable && !field.envelope) findings.push({ severity: 'error', code: 'VIEW_FILTER_NOT_FILTERABLE', path, message: `« ${fieldId} » n’est pas filtrable.` });
    else if (field.indexed === false) findings.push({ severity: 'warning', code: 'VIEW_FILTER_NOT_INDEXED', path, message: `${noun} porte sur « ${fieldId} », qu’aucun index ne sert.` });
  };
  asStrings(getAt(view, FILTERS)).forEach((fieldId, index) => filtered(`${FILTERS}/${index}`, fieldId, 'Le filtre'));
  ((getAt(view, QUICK) as Array<{ filter?: Record<string, unknown>; default?: boolean }> | undefined) ?? []).forEach((item, index) => Object.keys(item.filter ?? {}).forEach((fieldId) => filtered(`${QUICK}/${index}/filter/${fieldId}`, fieldId, 'Le filtre enregistré')));
  if (((getAt(view, QUICK) as Array<{ default?: boolean }> | undefined) ?? []).filter((item) => item.default === true).length > 1) findings.push({ severity: 'error', code: 'VIEW_QUICK_FILTER_DEFAULTS', path: QUICK, message: 'Un seul filtre enregistré par défaut.' });
  ((getAt(view, SEARCH) as { fields?: string[] } | undefined)?.fields ?? []).forEach((fieldId, index) => {
    const field = access(`${SEARCH}/fields/${index}`, fieldId, 'La recherche');
    if (field !== undefined && !field.searchable) findings.push({ severity: 'error', code: 'VIEW_SEARCH_NOT_SEARCHABLE', path: `${SEARCH}/fields/${index}`, message: `« ${fieldId} » n’est pas un champ texte cherchable.` });
  });
  ((getAt(view, SORT) as Array<{ field: string }> | undefined) ?? []).forEach((sort, index) => {
    const field = access(`${SORT}/${index}/field`, sort.field, 'Le tri');
    if (field?.indexed === false) findings.push({ severity: 'warning', code: 'VIEW_FILTER_NOT_INDEXED', path: `${SORT}/${index}/field`, message: `Le tri porte sur « ${sort.field} », qu’aucun index ne sert.` });
  });
  if (tableIndex(view) >= 0) asStrings(getAt(view, groupPointer(view))).forEach((fieldId, index, all) => filtered(`${groupPointer(view)}${all.length > 1 || Array.isArray(getAt(view, groupPointer(view))) ? `/${index}` : ''}`, fieldId, 'Le regroupement'));
  return findings;
}

/** Différences feuille par feuille (pointeurs JSON) ; une liste de longueur différente est un seul changement. */
export function diffPointers(base: unknown, draft: unknown, path = ''): string[] {
  if (JSON.stringify(base ?? null) === JSON.stringify(draft ?? null)) return [];
  if (Array.isArray(base) && Array.isArray(draft) && base.length === draft.length) return base.flatMap((item, index) => diffPointers(item, draft[index], `${path}/${index}`));
  if (isRecord(base) && isRecord(draft)) {
    const keys = [...new Set([...Object.keys(base), ...Object.keys(draft)])].sort();
    return keys.flatMap((key) => diffPointers(base[key], draft[key], formatPointer([...parsePointer(path), key])));
  }
  return [path === '' ? '/' : path];
}

/** Un changement de vue est toujours mineur (ADR-130, addendum du 2026-10-15) : présentation seulement. */
export function classifyView(base: View | null, draft: View): ChangeClass {
  return { structural: false, changes: diffPointers(base, draft).map((path) => ({ path, kind: 'label' as const })) };
}

/** Corps montré dans l'aperçu : la même liste, sans action, commande de masse, création ni export (lecture seule). */
export function previewBody(view: View): View {
  const body = JSON.parse(JSON.stringify(view)) as View & Record<string, unknown>;
  delete body.actions;
  delete body['overrides'];
  const inputs = body.collection?.inputs as Record<string, unknown> | undefined;
  if (inputs !== undefined) for (const key of ['bulk_commands', 'create_action', 'row_action', 'export']) delete inputs[key];
  if (body.collection !== undefined) delete (body.collection as { events?: unknown }).events;
  return body;
}
